import { Flex, Text } from '@mantine/core';
import { Button } from 'src/design-system/Button';
import { Modal } from 'src/design-system/Modal';
import type { SpeakerConflictResolution } from 'src/meetings/hooks/useAssignSpeaker';

type Props = {
  opened: boolean;
  // The person the adviser has just chosen, who already speaks on this transcript.
  personName: string;
  // The name the voice being changed currently shows, or null while nobody has identified it.
  currentSpeakerName: string | null;
  // Which of the two answers is in flight, so the spinner sits on the button that was pressed.
  pending: SpeakerConflictResolution | null;
  onCancel: () => void;
  onSwap: () => void;
  onMerge: () => void;
};

/**
 * Asked before an assignment that would put two of the transcript's voices under one name. Merging
 * is what assigning has always done, but the screen groups voices by the name they show, so once
 * merged the two cannot be told apart or separated again. Swapping is the way out: the two
 * speakers exchange names and both stay separate voices.
 */
export function SpeakerConflictModal({
  opened,
  personName,
  currentSpeakerName,
  pending,
  onCancel,
  onSwap,
  onMerge,
}: Props) {
  const swapOutcome = currentSpeakerName
    ? `Swapping them instead exchanges the two names: this voice becomes ${personName}, and the voice they have now becomes ${currentSpeakerName}.`
    : `Swapping them instead moves ${personName} onto this voice and leaves the voice they have now for you to identify.`;

  return (
    <Modal opened={opened} onClose={onCancel} title={`${personName} already speaks here`}>
      <Text size="sm" c="gray.7" mt="sm">
        Assigning this voice to {personName} merges it with what they have already said in this
        meeting. The two can&apos;t be separated again. {swapOutcome}
      </Text>

      <Flex justify="flex-end" align="center" gap="sm" mt="lg">
        <Button variant="text" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={pending !== null}
          loading={pending === 'merge'}
          onClick={onMerge}
        >
          Merge
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={pending !== null}
          loading={pending === 'swap'}
          onClick={onSwap}
        >
          Swap instead
        </Button>
      </Flex>
    </Modal>
  );
}
